// src/components/BlogController.ts
import { BlogUtils } from '../utils/blogUtils-new';
import SEOManager from '../utils/SEOManager';
import { Article } from '../types/Article';
import { articles } from '../data/articles';
import CarouselManager from '../utils/CarouselManager';

export class BlogController {
  private blogUtils = new BlogUtils();
  private seoManager = new SEOManager();
  private articleSlug: string | null = null;

  public initialize(): void {
    const path = window.location.pathname;
    if (path.includes('blog.html'))          this.renderBlogPage();
    else if (path.includes('single-article')) this.renderArticlePage();
  }

  /* ───────────────────────── BLOG LISTE ───────────────────────── */

  private renderBlogPage(): void {
    // Accès aux éléments du DOM
    const featured = document.querySelector('.featured-article') as HTMLElement;
    const recent = document.querySelector('.recent-articles') as HTMLElement;
    const all = document.querySelector('.all-articles') as HTMLElement;
    
    // Nouveaux éléments pour la version modernisée
    const filterContainer = document.getElementById('blog-filters') as HTMLElement;
    const modernGrid = document.getElementById('modern-blog-grid') as HTMLElement;

    // Vérification des éléments nécessaires
    if (!featured || !recent || !all) {
      console.error('Containers manquants');
      return;
    }

    if (!filterContainer || !modernGrid) {
      console.error('Containers modernisés manquants');
      return;
    }

    // Pas d'articles à afficher
    if (articles.length === 0) {
      this.handleEmptyArticles(featured, recent, all);
      modernGrid.innerHTML = '<div class="text-center py-8 text-white">Aucun article disponible pour le moment.</div>';
      return;
    }

    // Préparation des données
    const cards = articles.map(a => this.blogUtils.createArticleCard(a));
    
    // Extraction des catégories uniques pour les filtres
    const categories = [...new Set(articles
      .filter(article => article.category)
      .map(article => article.category))]
      .sort();
      
    // Rendu des filtres
    filterContainer.innerHTML = this.renderCategoryFilters(categories);
    
    // Rendu de la grille moderne
    modernGrid.innerHTML = this.renderModernCards(cards);
    
    // Ajout du JavaScript des filtres
    this.initializeFilters();
    
    // Conservation de la grille horizontale pour la section "Tous les articles"
    all.className = 'all-articles';
    all.innerHTML = this.renderHorizontalArticlesList(cards);
    
    // Initialiser le carousel avancé après insertion du HTML
    setTimeout(() => {
      CarouselManager.initializeCarousel(all);
    }, 100);
  }

  /* ───────────────────────── PAGE ARTICLE ─────────────────────── */

  private renderArticlePage(): void {
    const container = document.querySelector('.article-content') as HTMLElement;
    const all       = document.querySelector('.all-articles')    as HTMLElement;

    if (!container || !all) {
      console.error('Containers manquants');
      return;
    }

    const slug = new URLSearchParams(window.location.search).get('slug');
    this.articleSlug = slug;
    const article = articles.find(a => this.blogUtils.createSlug(a.title) === slug);

    if (!article) {
      this.handleArticleNotFound(container);
      return;
    }

    /* SEO */
    this.updateMetaTags(article);

    /* Contenu principal */
    container.innerHTML = this.renderArticleContent(article);

    /* Autres articles - Exclut l'article courant */
    const others = articles
      .filter(a => a !== article)
      .map(a => this.blogUtils.createArticleCard(a));

    all.className = 'all-articles';
    all.innerHTML = this.renderHorizontalArticlesList(others);
    
    // Initialiser le carousel avancé après insertion du HTML
    setTimeout(() => {
      CarouselManager.initializeCarousel(all);
    }, 100);
  }

  private renderHorizontalArticlesList(cards: any[]): string {
    return `
      <div class="carousel-container">
        <button class="carousel-nav-button carousel-prev" aria-label="Précédent">
          <svg viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        
        <div class="horizontal-articles-container">
          ${cards.map(card => `
            <div class="article-card">
              <a href="single-article.html?slug=${card.slug}" class="block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                <div class="h-40 overflow-hidden">
                  <img src="${card.image}" alt="Illustration de l'article ${card.title}" class="w-full h-full object-cover transition-transform duration-500 hover:scale-110">
                </div>
                <div class="p-4">
                  <time class="text-sm text-gray-400">${card.date}</time>
                  <h3 class="font-semibold text-gray-800 text-lg mt-2 line-clamp-2 min-h-[3.5rem]">${card.title}</h3>
                  <p class="text-gray-600 mt-2 text-sm line-clamp-3 min-h-[4.5rem]">${card.excerpt}</p>
                  <div class="mt-3">
                    <span class="inline-flex items-center text-[#ed4c67] hover:text-[#3498db] transition-colors">
                      Lire la suite
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 ml-1">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            </div>
          `).join('')}
        </div>
        
        <button class="carousel-nav-button carousel-next" aria-label="Suivant">
          <svg viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
      
      <div class="carousel-pagination">
        <span class="dot active" data-index="0"></span>
        <span class="dot" data-index="1"></span>
        <span class="dot" data-index="2"></span>
      </div>
    `;
  }

  /* ───────────────────────── HELPERS UI ───────────────────────── */

  /**
   * Génère le HTML pour les filtres par catégorie
   */
  private renderCategoryFilters(categories: string[]): string {
    return `
      <button class="blog-filter-button active" data-category="all">Tous</button>
      ${categories.map(category => 
        `<button class="blog-filter-button" data-category="${category}">${category}</button>`
      ).join('')}
    `;
  }
  
  /**
   * Génère le HTML pour les cartes d'articles modernisées
   */
  private renderModernCards(cards: any[]): string {
    return cards.map(card => {
      const article = articles.find(a => this.blogUtils.createSlug(a.title) === card.slug);
      const category = article?.category || 'Article';
      const readTime = article?.metadata?.readingTime || '5';
      
      return `
        <a href="single-article.html?slug=${card.slug}" class="modern-blog-card" data-category="${category}">
          <div class="modern-blog-card-image">
            <img src="${card.image}" alt="Illustration de l'article ${card.title}" loading="lazy" />
            <span class="modern-blog-card-category">${category}</span>
          </div>
          <div class="modern-blog-card-content">
            <div class="modern-blog-card-meta">
              <div class="modern-blog-card-date">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                ${card.date}
              </div>
              <div class="modern-blog-card-readtime">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                ${readTime} min
              </div>
            </div>
            <h3 class="modern-blog-card-title">${card.title}</h3>
            <p class="modern-blog-card-excerpt">${card.excerpt}</p>
            <div class="modern-blog-card-read-more">
              Lire la suite
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
              </svg>
            </div>
          </div>
        </a>
      `;
    }).join('');
  }
  
  /**
   * Initialise le système de filtrage par catégorie
   */
  private initializeFilters(): void {
    const filterButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.blog-filter-button'));
    const blogCards = Array.from(document.querySelectorAll<HTMLElement>('.modern-blog-card'));

    if (filterButtons.length === 0 || blogCards.length === 0) return;

    filterButtons.forEach(button => {
      button.addEventListener('click', () => {
        filterButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        const category = button.getAttribute('data-category');

        blogCards.forEach(card => {
          const cardCategory = card.getAttribute('data-category');

          if (!category || category === 'all' || cardCategory === category) {
            card.style.display = '';
            card.classList.remove('filtered-out');
          } else {
            card.classList.add('filtered-out');
            setTimeout(() => {
              card.style.display = 'none';
            }, 250);
          }
        });
      });
    });
  }

  /**
   * Met à jour les meta tags pour le référencement
   */
  private updateMetaTags(article: Article): void {
    const slug = this.articleSlug || this.blogUtils.createSlug(article.title);
    const seoConfig = this.seoManager.getArticleSEO(article, slug);
    const resolvedImage = this.blogUtils.resolveAssetPath(article.image);
    if (resolvedImage) {
      seoConfig.image = resolvedImage;
    }
    this.seoManager.updateMetaTags(seoConfig);
  }

  /**
   * Génère le HTML d'un article complet (page single‑article.html)
   */
  private renderArticleContent(article: Article): string {
    const featuredImage = this.blogUtils.resolveAssetPath(article.image)
      || this.blogUtils.resolveAssetPath('/src/assets/optimized/AccueilWONNew-800w.webp');
    const resolvedContent = this.blogUtils.resolveContentAssets(article.content);

    return `
      <div class="max-w-4xl mx-auto px-4 relative">
          <header class="article-header relative">
            <h1 class="article-title">
              ${article.title}
            </h1>
            
            <div class="article-meta">
              <time>${article.date}</time>
              ${article.metadata?.author
                ? `<span>Par ${article.metadata.author}</span>`
                : ''}
              ${article.metadata?.readingTime
                ? `<span class="reading-time">${article.metadata.readingTime} min de lecture</span>`
                : ''}
            </div>
          </header>
        
        <!-- Image mise en avant avec effet dynamique -->
        <div class="featured-image-container">
          <img src="${featuredImage}" alt="Illustration principale de l'article ${article.title}" class="w-full h-full object-cover">
        </div>
  
        <!-- Contenu avec classes améliorées -->
        <div class="article-content prose prose-lg prose-invert max-w-none">
          ${resolvedContent}
        </div>
  
        <!-- Tags supprimés pour éviter doublon -->
        ${
          false && article.metadata?.tags
            ? `<div class="article-tags">
                 <h3>Tags</h3>
                 <div class="flex flex-wrap">
                   ${article.metadata.tags
                     .map(
                       tag =>
                         `<span class="tag">${tag}</span>`
                     )
                     .join('')}
                 </div>
               </div>`
            : ''
        }
      </div>
    `;
  }

  /**
   * Affiche un message lorsqu'il n'y a pas d'articles
   */
  private handleEmptyArticles(featured: HTMLElement, recent: HTMLElement, all: HTMLElement): void {
    const emptyMessage = `
        <div class="text-center py-12">
            <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1M19 8l-7 5-7-5m14 0v8a2 2 0 01-2 2h-3"></path>
            </svg>
            <h2 class="text-2xl font-bold mb-4 mt-4">Aucun article disponible</h2>
            <p class="text-gray-500">Des articles seront bientôt publiés. Revenez nous voir prochainement !</p>
        </div>
    `;
    featured.innerHTML = emptyMessage;
    recent.innerHTML = '';
    all.innerHTML = '';
  }

  /**
   * Affiche un message lorsqu'un article n'est pas trouvé
   */
  private handleArticleNotFound(container: HTMLElement): void {
    container.innerHTML = `
        <div class="text-center py-12">
            <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h2 class="text-2xl font-bold mb-4 mt-4">Article non trouvé</h2>
            <p class="text-gray-600 mb-6">L'article que vous recherchez n'existe pas ou a été déplacé.</p>
            <a href="blog.html" class="inline-block px-6 py-3 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-colors">
                Retour au blog
            </a>
        </div>
    `;
  }

  /**
   * Affiche un message d'erreur
   */
  private handleError(container: HTMLElement, container2?: HTMLElement, container3?: HTMLElement): void {
    const errorMessage = `
        <div class="text-center py-12">
            <svg class="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <h2 class="text-2xl font-bold mb-4 mt-4 text-red-600">Une erreur est survenue</h2>
            <p class="text-gray-600">Veuillez réessayer plus tard ou contacter notre équipe.</p>
            <a href="index.html" class="inline-block mt-6 px-6 py-3 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-colors">
                Retour à l'accueil
            </a>
        </div>
    `;

    container.innerHTML = errorMessage;
    if (container2) container2.innerHTML = '';
    if (container3) container3.innerHTML = '';
  }
}
